import { ISource, TReconciler } from './types';

/**
 * Fetch N sources concurrently and reconcile them into one domain model.
 *
 * - Plain async function (NOT a hook) so both sagas and React Query useCases can call it.
 * - Enabled sources fire concurrently; disabled sources are skipped (result `null`).
 * - A `required` source that rejects fails the whole compose; an optional source that
 *   rejects degrades to `null` so a single flaky source can't break the read.
 *
 * The reconciler receives `{ [source.id]: result | null }` for EVERY declared source.
 */
export async function composeSources<M>(
  sources: ISource<unknown>[],
  reconcile: TReconciler<M>,
): Promise<M> {
  const active = sources.filter((s) => s.enabled?.() ?? true);

  const settled = await Promise.all(
    active.map((s) => (s.required ? s.fetch() : s.fetch().catch(() => null))),
  );

  // Seed every declared source (incl. disabled) to null, then overlay active results,
  // so the reconciler can rely on all keys being present.
  const results: Record<string, unknown> = {};
  sources.forEach((s) => {
    results[s.id] = null;
  });
  active.forEach((s, i) => {
    results[s.id] = settled[i];
  });

  return reconcile(results);
}
